<template>
  <div class="promotional-badge-container">
    <!-- Main promotional badge -->
    <div
      v-if="showBadge"
      class="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold"
      :class="badgeClasses"
      :data-testid="testId"
    >
      <svg
        v-if="showIcon"
        class="w-3 h-3 mr-1"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd"
        />
      </svg>
      {{ badgeText }}
    </div>

    <!-- Discount percentage overlay (for discount type promotions) -->
    <div
      v-if="showDiscountPercentage && discountPercentage"
      class="inline-flex items-center px-1.5 py-0.5 ml-1 rounded text-xs font-bold bg-red-500 text-white"
      data-testid="discount-percentage"
    >
      {{ discountPercentage }}% OFF
    </div>
  </div>
</template>

<script>
import { computed } from "vue";

export default {
  name: "PromotionalBadge",
  props: {
    /**
     * Type of promotional badge to display
     * @type {'first-lesson' | 'discount' | 'free' | 'custom'}
     */
    type: {
      type: String,
      default: "first-lesson",
      validator: (value) =>
        ["first-lesson", "discount", "free", "custom"].includes(value),
    },
    /**
     * Custom text for the badge (overrides default text based on type)
     */
    customText: {
      type: String,
      default: "",
    },
    /**
     * Discount percentage for display (e.g., 50 for 50% off)
     */
    discountPercentage: {
      type: Number,
      default: null,
    },
    /**
     * Show discount percentage as separate badge
     */
    showDiscountPercentage: {
      type: Boolean,
      default: false,
    },
    /**
     * Theme variant for styling
     * @type {'primary' | 'success' | 'warning' | 'info'}
     */
    variant: {
      type: String,
      default: "primary",
      validator: (value) =>
        ["primary", "success", "warning", "info"].includes(value),
    },
    /**
     * Size of the badge
     * @type {'sm' | 'md' | 'lg'}
     */
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg"].includes(value),
    },
    /**
     * Show icon in badge
     */
    showIcon: {
      type: Boolean,
      default: true,
    },
    /**
     * Hide badge completely
     */
    hidden: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const showBadge = computed(() => !props.hidden);

    const badgeText = computed(() => {
      if (props.customText) {
        return props.customText;
      }

      switch (props.type) {
        case "first-lesson":
          return props.discountPercentage
            ? `${props.discountPercentage}% Off First Lesson`
            : "First Lesson Promotion";
        case "discount":
          return props.discountPercentage
            ? `${props.discountPercentage}% Discount`
            : "Special Discount";
        case "free":
          return "Free First Lesson";
        case "custom":
          return "Special Offer";
        default:
          return "Promotion";
      }
    });

    const badgeClasses = computed(() => {
      const baseClasses = "transition-all duration-200";

      // Size classes
      const sizeClasses = {
        sm: "px-1.5 py-0.5 text-xs",
        md: "px-2 py-1 text-xs",
        lg: "px-3 py-1.5 text-sm",
      };

      // Variant classes
      const variantClasses = {
        primary: "bg-blue-100 text-blue-800 border border-blue-200",
        success: "bg-green-100 text-green-800 border border-green-200",
        warning: "bg-yellow-100 text-yellow-800 border border-yellow-200",
        info: "bg-indigo-100 text-indigo-800 border border-indigo-200",
      };

      return [
        baseClasses,
        sizeClasses[props.size],
        variantClasses[props.variant],
      ].join(" ");
    });

    const testId = computed(() => {
      return `promotional-badge-${props.type}`;
    });

    return {
      showBadge,
      badgeText,
      badgeClasses,
      testId,
    };
  },
};
</script>

<style scoped>
.promotional-badge-container {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
}

/* Hover effects for interactive badges */
.promotional-badge-container:hover .inline-flex:first-child {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Animation for discount percentage */
@keyframes pulse-discount {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.8;
  }
}

[data-testid="discount-percentage"] {
  animation: pulse-discount 2s infinite;
}

/* Responsive adjustments */
@media (max-width: 640px) {
  .promotional-badge-container {
    font-size: 0.75rem;
  }
}
</style>
